import React from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import StatusIcon from 'src/components/StatusIcon';
import {
  IconStatusAndColor,
  IconStatusAndColorVariants
} from 'src/utils/constants/common';
import { Edit, Replay } from '@mui/icons-material';

export const AutomaticDebitCollectionStatus = {
  PENDING: 'PENDING',
  PROCESSED: 'PROCESSED',
  DEBITED: 'DEBITED',
  FAILED_TO_BE_PROCESSED: 'FAILED_TO_BE_PROCESSED',
  FAILED_TO_BE_DEBITED: 'FAILED_TO_BE_DEBITED',
  CANCELLED: 'CANCELLED'
};

export const AutomaticDebitCollectionOptions = {
  CANCEL: 'CANCEL',
  SET_NEW_DUE_DATE: 'SET_NEW_DUE_DATE',
  SEND_TO_REBILL: 'SEND_TO_REBILL',
  REACTIVATE: 'REACTIVATE',
  EDIT_AMOUNT: 'EDIT_AMOUNT'
};

export const getAutomaticDebitCollectionStatus = status => {
  switch (status) {
    case AutomaticDebitCollectionStatus.PROCESSED:
      return (
        <StatusIcon
          type={IconStatusAndColorVariants.LIGHT.OK.status}
          description={`ENUMS.AUTOMATIC_DEBIT_STATUS.${status}`}
          color={IconStatusAndColorVariants.LIGHT.OK.color}
        />
      );
    case AutomaticDebitCollectionStatus.DEBITED:
      return (
        <StatusIcon
          type={IconStatusAndColor.OK.status}
          description={`ENUMS.AUTOMATIC_DEBIT_STATUS.${status}`}
        />
      );

    case AutomaticDebitCollectionStatus.FAILED_TO_BE_PROCESSED:
    case AutomaticDebitCollectionStatus.FAILED_TO_BE_DEBITED:
      return (
        <StatusIcon
          type={IconStatusAndColor.ERROR.status}
          description={`ENUMS.AUTOMATIC_DEBIT_STATUS.${status}`}
        />
      );

    case AutomaticDebitCollectionStatus.CANCELLED:
      return (
        <StatusIcon
          type={IconStatusAndColorVariants.LIGHT.ERROR.status}
          description={`ENUMS.AUTOMATIC_DEBIT_STATUS.${status}`}
          color={IconStatusAndColorVariants.LIGHT.ERROR.color}
        />
      );

    case AutomaticDebitCollectionStatus.PENDING:
      return (
        <StatusIcon
          type={IconStatusAndColor.WARNING.status}
          description={`ENUMS.AUTOMATIC_DEBIT_STATUS.${status}`}
        />
      );

    default:
      return (
        <StatusIcon
          type={IconStatusAndColor.NONE.status}
          description="COMMON.NO_DATA"
        />
      );
  }
};

export const getAutomaticDebitCollectionMenuOptions = status => {
  const statusOptions = {
    [AutomaticDebitCollectionStatus.FAILED_TO_BE_DEBITED]: [
      {
        name: AutomaticDebitCollectionOptions.SEND_TO_REBILL,
        label: `ENUMS.AUTOMATIC_DEBIT_COLLECTION_ACTIONS.${AutomaticDebitCollectionOptions.SEND_TO_REBILL}`,
        icon: <Replay />
      }
    ],
    [AutomaticDebitCollectionStatus.FAILED_TO_BE_PROCESSED]: [
      {
        name: AutomaticDebitCollectionOptions.SEND_TO_REBILL,
        label: `ENUMS.AUTOMATIC_DEBIT_COLLECTION_ACTIONS.${AutomaticDebitCollectionOptions.SEND_TO_REBILL}`,
        icon: <Replay />
      }
    ],
    [AutomaticDebitCollectionStatus.PENDING]: [
      {
        name: AutomaticDebitCollectionOptions.CANCEL,
        label: `ENUMS.AUTOMATIC_DEBIT_COLLECTION_ACTIONS.${AutomaticDebitCollectionOptions.CANCEL}`,
        icon: <CancelIcon />
      },
      {
        name: AutomaticDebitCollectionOptions.SET_NEW_DUE_DATE,
        label: `ENUMS.AUTOMATIC_DEBIT_COLLECTION_ACTIONS.${AutomaticDebitCollectionOptions.SET_NEW_DUE_DATE}`,
        icon: <CalendarMonthIcon />
      },
      {
        name: AutomaticDebitCollectionOptions.EDIT_AMOUNT,
        label: `ENUMS.AUTOMATIC_DEBIT_COLLECTION_ACTIONS.${AutomaticDebitCollectionOptions.EDIT_AMOUNT}`,
        icon: <Edit />
      }
    ],
    [AutomaticDebitCollectionStatus.CANCELLED]: [
      {
        name: AutomaticDebitCollectionOptions.REACTIVATE,
        label: `ENUMS.AUTOMATIC_DEBIT_COLLECTION_ACTIONS.${AutomaticDebitCollectionOptions.REACTIVATE}`,
        icon: <Replay />
      }
    ]
  };

  return statusOptions[status] || [];
};

export default AutomaticDebitCollectionStatus;

export function isBankCodeValid(clabeOrAccountNumber, bankName, banksCatalog) {
  if (!clabeOrAccountNumber || !bankName) return false;

  const selectedBank = banksCatalog.find(bank => bank.name === bankName);
  if (!selectedBank) return false;

  const bankCode = clabeOrAccountNumber.substring(0, 3);
  return bankCode === selectedBank.code;
}

export const calculateReconciledAmount = (
  invoices,
  unreconciledAmount,
  excludeIndex = -1
) => {
  let total = 0;
  const invoiceList = invoices || [];

  invoiceList.forEach((invoice, index) => {
    if (index !== excludeIndex && invoice && invoice.amountToPay) {
      const remainingAmount = unreconciledAmount - total;
      const amountToAdd = Math.min(invoice.amountToPay, remainingAmount);
      total += amountToAdd > 0 ? amountToAdd : 0;
    }
  });

  return total;
};


export const getTotalReconciledAmount = (invoices, unreconciledAmount) => {
  return calculateReconciledAmount(invoices, unreconciledAmount);
};

export const getRemainingAmount = (
  invoices,
  unreconciledAmount,
  excludeIndex = -1
) => {
  const total = calculateReconciledAmount(
    invoices,
    unreconciledAmount,
    excludeIndex
  );
  return unreconciledAmount - total;
};


export const calculateTotalOriginalAmounts = invoices => {
  return (invoices || []).reduce((sum, invoice) => {
    if (invoice && invoice.amountIvaIncluded) {
      return sum + invoice.amountIvaIncluded;
    }
    return sum;
  }, 0);
};

export const checkAmountExceeds = (invoices, unreconciledAmount) => {
  const invoiceList = invoices || [];
  let anyInvoiceExceeds = false;
  let cumulativeAmount = 0;

  invoiceList.forEach(invoice => {
    if (invoice) {
      const invoiceOriginalAmount = invoice.amountIvaIncluded || 0;
      const remainingForInvoice = unreconciledAmount - cumulativeAmount;

      if (invoiceOriginalAmount > remainingForInvoice) {
        anyInvoiceExceeds = true;
      }
      cumulativeAmount += Math.min(invoiceOriginalAmount, remainingForInvoice);
    }
  });

  const totalOriginalAmounts = calculateTotalOriginalAmounts(invoices);
  const exceedsAmount =
    totalOriginalAmounts > unreconciledAmount || anyInvoiceExceeds;

  return exceedsAmount;
};